import { BadgeCheck, ShieldCheck, FileText, Clock, Stethoscope } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

const About = () => {
  const infoCards = [
    {
      icon: BadgeCheck,
      title: "Licensed Attending Physicians",
      description:
        "Certified Brazilian medical doctors with post-graduate training in Family Medicine and acute care, registered with the Regional Medical Council."
    },
    {
      icon: ShieldCheck,
      title: "Official Digital Prescriptions",
      description:
        "ICP-Brasil signed prescriptions with QR-code verification, legally valid at licensed pharmacies across all 26 Brazilian states."
    },
    {
      icon: FileText,
      title: "Insurance-Ready Documentation",
      description:
        "Itemized receipts and English clinical summaries, ready for submission to your travel insurance provider."
    },
    {
      icon: Clock,
      title: "Structured Daily Availability",
      description:
        "Centralized triage and medical appointments 7 days a week, 07:00–22:00 BRT, with an average reply in 30 minutes."
    }
  ];

  const specialties = [
    "Family Medicine (Adults & Children)",
    "Preventive Medicine",
    "Telemedicine"
  ];

  return (
    <section id="about" className="py-20 bg-section-bg border-y border-border">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-4">
              About Rio Med Care
            </h2>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto">
              Private English-speaking medical care for international travelers, expats and global organizations in Brazil.
            </p>
            <p className="text-lg text-text-secondary max-w-4xl mx-auto mt-6 leading-relaxed">
              Rio Med Care is an independent medical practice dedicated to providing immediate, high-standard healthcare
              for international visitors across Brazil. Delivering prompt telehealth consultations nationwide, our clinical operations follow strict international care protocols
              and Brazilian Federal Council of Medicine (CFM) regulations.
            </p>
          </div>

          {/* Institutional standards grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {infoCards.map((card, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <card.icon className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="font-semibold text-text-primary mb-2">{card.title}</h3>
                  <p className="text-sm text-text-secondary">{card.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Specialties */}
          <div className="text-center mb-12">
            <h3 className="text-xl font-semibold text-text-primary mb-6">Areas of Care</h3>
            <div className="flex flex-wrap justify-center gap-3">
              {specialties.map((specialty, index) => (
                <div
                  key={index}
                  className="bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium border border-primary/20"
                >
                  <Stethoscope className="inline h-4 w-4 mr-2" />
                  {specialty}
                </div>
              ))}
            </div>
          </div>

          {/* Medical governance & compliance */}
          <div className="max-w-3xl mx-auto rounded-xl border border-border bg-background p-6 text-center">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-text-primary mb-3">
              Medical Governance & Legal Compliance
            </h3>
            <p className="text-sm text-text-secondary leading-relaxed">
              Technical Director: Dr. Luis Santos, MD — Board Certified in Family &amp; Community Medicine
              (CRM-RJ 52-0115383-8). All consultations and prescriptions comply with CFM Telemedicine
              Resolution 2.314/2022.
            </p>
            <a
              href="https://portal.cfm.org.br/busca-medicos/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary hover:underline mt-3 inline-block"
            >
              Verify at portal.cfm.org.br →
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
